import React, { useMemo, useState } from 'react';
import { Tour } from '../../types';
import { getTourAutoDeleteDate, isTourArchived } from '../../utils/tourStatus';

interface TourListProps {
  tours: Tour[];
  onUpdateTour: (tour: Tour) => void;
  onDeleteTour: (tourId: string) => void;
  loading?: boolean;
}

type TourTab = 'active' | 'archived';

const TourList: React.FC<TourListProps> = ({ tours, onUpdateTour, onDeleteTour, loading = false }) => {
  const [deleteConfirm, setDeleteConfirm] = useState<{ tourId: string; tourName: string } | null>(null);
  const [activeTab, setActiveTab] = useState<TourTab>('active');

  const { activeTours, archivedTours } = useMemo(() => {
    const active: Tour[] = [];
    const archived: Tour[] = [];
    tours.forEach((tour) => {
      if (isTourArchived(tour)) {
        archived.push(tour);
      } else {
        active.push(tour);
      }
    });
    return { activeTours: active, archivedTours: archived };
  }, [tours]);

  const displayedTours = activeTab === 'active' ? activeTours : archivedTours;

  const formatDate = (dateString: string | undefined): string => {
    if (!dateString) return 'Not set';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return 'Invalid Date';
    }
  };

  const getStatusBadge = (status: string, archived: boolean) => {
    if (archived) {
      return (
        <span className="px-2 py-1 text-xs font-medium rounded-full border bg-amber-100 text-amber-900 border-amber-200">
          Archived
        </span>
      );
    }

    const statusClasses = {
      active: 'bg-green-100 text-green-800 border-green-200',
      inactive: 'bg-gray-100 text-gray-800 border-gray-200',
      sold_out: 'bg-red-100 text-red-800 border-red-200',
      published: 'bg-blue-100 text-blue-800 border-blue-200',
      draft: 'bg-yellow-100 text-yellow-800 border-yellow-200'
    };

    const statusLabels = {
      active: 'Active',
      inactive: 'Inactive',
      sold_out: 'Sold Out',
      published: 'Published',
      draft: 'Draft'
    };

    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full border ${statusClasses[status as keyof typeof statusClasses] || 'bg-gray-100 text-gray-800 border-gray-200'}`}>
        {statusLabels[status as keyof typeof statusLabels] || status}
      </span>
    );
  };

  const handleDeleteClick = (tour: Tour) => {
    setDeleteConfirm({ tourId: tour.tourId, tourName: tour.tourName });
  };

  const handleDeleteConfirm = () => {
    if (deleteConfirm) {
      onDeleteTour(deleteConfirm.tourId);
      setDeleteConfirm(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteConfirm(null);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--primary-deep-blue)]"></div>
        <span className="ml-2 text-[var(--secondary-slate)]">Loading tours...</span>
      </div>
    );
  }

  if (tours.length === 0) {
    return (
      <div className="text-center py-8">
        <p className="text-[var(--secondary-slate)]">No tours found. Create your first tour above!</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Active / Archived tabs */}
      <div className="flex border-b border-[var(--neutral-medium-gray)]">
        <button
          type="button"
          onClick={() => setActiveTab('active')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'active'
              ? 'border-[var(--primary-deep-blue)] text-[var(--primary-deep-blue)]'
              : 'border-transparent text-[var(--secondary-slate)] hover:text-[var(--primary-deep-blue)]'
          }`}
        >
          Active ({activeTours.length})
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('archived')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'archived'
              ? 'border-[var(--primary-deep-blue)] text-[var(--primary-deep-blue)]'
              : 'border-transparent text-[var(--secondary-slate)] hover:text-[var(--primary-deep-blue)]'
          }`}
        >
          Archived ({archivedTours.length})
        </button>
      </div>

      {activeTab === 'archived' && (
        <p className="text-sm text-[var(--secondary-slate)]">
          Tours move here automatically once their return date has passed. They stay for 2 years, then are permanently deleted.
        </p>
      )}

      {displayedTours.length === 0 ? (
        <div className="text-center py-8">
          <p className="text-[var(--secondary-slate)]">
            {activeTab === 'active'
              ? 'No active tours. Create a new tour above!'
              : 'No archived tours yet.'}
          </p>
        </div>
      ) : (
        displayedTours.map((tour) => {
          const archived = isTourArchived(tour);
          const autoDeleteDate = archived ? getTourAutoDeleteDate(tour) : null;

          return (
            <div key={tour.tourId} className="card">
              <div className="card-content">
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                  {/* Tour Info */}
                  <div className="flex-1">
                    <div className="flex items-start justify-between mb-4">
                      <h3 className="heading-tertiary text-[var(--primary-deep-blue)]">
                        {tour.tourName}
                      </h3>
                      {getStatusBadge(tour.status, archived)}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
                      <div className="flex flex-col">
                        <span className="font-medium text-[var(--primary-deep-blue)] mb-1">Tagline</span>
                        <span className="text-[var(--secondary-slate)]">{tour.tagline || '—'}</span>
                      </div>

                      <div className="flex flex-col">
                        <span className="font-medium text-[var(--primary-deep-blue)] mb-1">Destination</span>
                        <span className="text-[var(--secondary-slate)]">{tour.destination}</span>
                      </div>

                      <div className="flex flex-col">
                        <span className="font-medium text-[var(--primary-deep-blue)] mb-1">Duration</span>
                        <span className="text-[var(--secondary-slate)]">{tour.durationDays}D/{tour.durationNights}N</span>
                      </div>

                      <div className="flex flex-col">
                        <span className="font-medium text-[var(--primary-deep-blue)] mb-1">Price</span>
                        <span className="text-[var(--secondary-slate)]">₹{tour.price.toLocaleString()}</span>
                      </div>

                      <div className="flex flex-col">
                        <span className="font-medium text-[var(--primary-deep-blue)] mb-1">Available Slots</span>
                        <span className="text-[var(--secondary-slate)]">
                          {tour.availableSlots === undefined && tour.maxGroupSize === undefined
                            ? '—'
                            : `${tour.availableSlots ?? '—'}/${tour.maxGroupSize ?? '—'}`}
                        </span>
                      </div>

                      <div className="flex flex-col">
                        <span className="font-medium text-[var(--primary-deep-blue)] mb-1">Website badge</span>
                        <span className="text-[var(--secondary-slate)]">
                          {typeof tour.urgencySlots === 'number' && tour.urgencySlots > 0
                            ? `Only ${tour.urgencySlots} slots left`
                            : '—'}
                        </span>
                      </div>

                      <div className="flex flex-col">
                        <span className="font-medium text-[var(--primary-deep-blue)] mb-1">Departure</span>
                        <span className="text-[var(--secondary-slate)]">{formatDate(tour.departureDate)}</span>
                      </div>

                      <div className="flex flex-col">
                        <span className="font-medium text-[var(--primary-deep-blue)] mb-1">Return</span>
                        <span className="text-[var(--secondary-slate)]">{formatDate(tour.returnDate)}</span>
                      </div>
                    </div>

                    <div className="mt-4">
                      <div className="flex flex-col">
                        <span className="font-medium text-[var(--primary-deep-blue)] mb-1">Registration Deadline</span>
                        <span className="text-[var(--secondary-slate)]">{formatDate(tour.deadline)}</span>
                      </div>
                    </div>

                    {archived && autoDeleteDate && (
                      <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-md">
                        <p className="text-sm text-amber-900">
                          Hidden from the public site. Permanently deleted on{' '}
                          <strong>{formatDate(autoDeleteDate.toISOString())}</strong>
                          {' '}(2 years after return).
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Action Buttons */}
                  <div className="flex-shrink-0 flex flex-col gap-2">
                    <button
                      onClick={() => onUpdateTour(tour)}
                      className="btn btn-primary"
                    >
                      Update Tour
                    </button>
                    <button
                      onClick={() => handleDeleteClick(tour)}
                      className="btn btn-danger"
                    >
                      Delete Tour
                    </button>
                  </div>
                </div>
              </div>
            </div>
          );
        })
      )}

      {/* Delete Confirmation Dialog */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="heading-tertiary text-[var(--primary-deep-blue)] mb-4">
              Confirm Delete
            </h3>
            <p className="body-regular text-[var(--secondary-slate)] mb-6">
              Are you sure you want to delete the tour "{deleteConfirm.tourName}"? This action cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={handleDeleteCancel}
                className="btn btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteConfirm}
                className="btn btn-danger"
              >
                Delete Tour
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TourList;
